import React, { useState, useEffect } from 'react';
import { Locale, ViewMode } from '../types';
import { SiteContent } from '../data/content';

interface HeaderProps {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  siteContent: SiteContent;
}

export const Header: React.FC<HeaderProps> = ({
  locale,
  onLocaleChange,
  viewMode,
  onViewModeChange,
  siteContent,
}) => {
  const { header } = siteContent;
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 48);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`topbar ${isScrolled ? 'topbar--scrolled' : ''}`}
      role="banner"
    >
      <div className="topbar-left">
        <a href="#hero-title" className="mark" aria-label="Dmitry Yakovlev / Support Systems">
          DY<span>/</span>
        </a>

        {/* Mode Switcher: 30 SEC / FULL CV */}
        <div
          className="mode-toggle"
          role="group"
          aria-label="CV presentation depth"
        >
          <button
            type="button"
            className={`mode-toggle-btn ${viewMode === '30sec' ? 'is-active' : ''}`}
            onClick={() => onViewModeChange('30sec')}
            aria-pressed={viewMode === '30sec'}
          >
            <span className="mode-toggle-dot" aria-hidden="true" />
            {header.mode30s}
          </button>
          <button
            type="button"
            className={`mode-toggle-btn ${viewMode === 'full' ? 'is-active' : ''}`}
            onClick={() => onViewModeChange('full')}
            aria-pressed={viewMode === 'full'}
          >
            {header.modeFull}
          </button>
        </div>
      </div>

      <nav className="topbar-nav" aria-label="Primary navigation">
        <a href="#proof">{header.nav.scale}</a>
        <a href="#work">{header.nav.work}</a>
        <a href="#incident">{header.nav.incident}</a>
        {viewMode === 'full' && (
          <>
            <a href="#experience">{header.nav.experience}</a>
            <a href="#cases">{header.nav.cases}</a>
            <a href="#principles">{header.nav.principles}</a>
            <a href="#stack">{header.nav.stack}</a>
          </>
        )}
        <a href="#contact">{header.nav.contact}</a>
      </nav>

      {/* RU / EN Language Switch */}
      <div className="locale-switch" aria-label="Language Selector">
        <button
          type="button"
          className={`locale-btn ${locale === 'ru' ? 'is-active' : ''}`}
          onClick={() => onLocaleChange('ru')}
          aria-current={locale === 'ru' ? 'true' : undefined}
        >
          RU
        </button>
        <span className="locale-sep" aria-hidden="true">/</span>
        <button
          type="button"
          className={`locale-btn ${locale === 'en' ? 'is-active' : ''}`}
          onClick={() => onLocaleChange('en')}
          aria-current={locale === 'en' ? 'true' : undefined}
        >
          EN
        </button>
      </div>
    </div>
  );
};
